import type { ActionContext } from "vuex";
import type { RootState } from "@/store/types";
import type { TracesState } from "@/store/traces/state";
import type { DateTime } from "luxon";
import { computed } from "vue";
import { gql } from "graphql-tag";
import apolloClient from "@/apollo";
import type {
  ColumnarTraces,
  GetFilteredTracesV2,
  GotFilteredTraces,
} from "@/models/traces";
import type { DataUsage } from "@/models/common";
import { Mutex } from "async-mutex";
import { QueryData } from "@/warehouse/QueryData";
import { RandomSelection } from "@/algorithms/loadingStrategies";
import * as duckDB from "@/duckDB";

const getFilteredTracesQueryV2 = gql`
  query GetFilteredTraces($input: GetFilteredTracesV2) {
    getFilteredTracesV2(input: $input) {
      __typename
      dataUsage {
        dataProcessed
        dataReturned
      }
      pageInfo {
        hasNextPage
        endCursor
        hasPreviousPage
        startCursor
      }
      columnarTraces {
        attributes {
          key
          value
        }
        dynamicColumns {
          ... on StringDynamicColumn {
            __typename
            name
            values
          }
        }
        elapsed
        ends
        filenameDistribution {
          filename
          values
        }
        id
        label
        language
        libraryName
        libraryVersion
        name
        resourceAttributes {
          key
          value
        }
        serviceInstanceId
        serviceName
        serviceNamespace
        serviceVersion
        starts
        statusCode
        statusMessage
      }
    }
  }
`;

const mutex = new Mutex();

/**
 * Execute the query getFilteredTraces and assigns the Traces to the warehouse.
 * It also keeps pulling query objects from Vuex until all parquet files were loaded.
 * @param context - Traces Vuex context from where the function is being called.
 * @param input - Filename, pageCursor and filters to be applied in the query.
 * @param thisQueryStartedAt - DateTime at which time this query began
 *    executing. Used to prevent old queries from conflicting with more recent
 *    ones due to network race conditions on results.
 * @returns Void or an object with the error message is any.
 */
export default async function executeTracesQuery(
  context: ActionContext<TracesState, RootState>,
  input: GetFilteredTracesV2,
  thisQueryStartedAt: DateTime
): Promise<void | { message: string }> {
  try {
    const loadingStrategy = new RandomSelection();
    if (
      shouldStopQuery(context, thisQueryStartedAt) ||
      queryLimitReached(context)
    ) {
      context.commit("setLoading", false);
      QueryData.updateQueryProgressState("traces", input.filename);
      return;
    }

    const results = await apolloClient.query({
      query: getFilteredTracesQueryV2,
      variables: {
        input: input,
      },
      fetchPolicy: "no-cache",
    });
    const gotTraces: GotFilteredTraces = results.data.getFilteredTracesV2;
    if (!context.getters["getLoading"]) context.commit("setLoading", true);
    if (
      shouldStopQuery(context, thisQueryStartedAt) ||
      queryLimitReached(context)
    ) {
      context.commit("setLoading", false);
      QueryData.updateQueryProgressState("traces", input.filename);
      return;
    }

    // tableCreated is used to prevent updating table with data that was just
    // used to create the table
    const hasData = Object.values(gotTraces.columnarTraces)
      .filter((v) => Array.isArray(v))
      .some((v) => v.length > 0);
    if (hasData) {
      // TODO: Traces parallelization is affected by this exclusive section, fix it
      await mutex.runExclusive(async () => {
        let tableCreated = false;
        const tableExists = await duckDB.tableExists("traces");
        if (!tableExists) {
          await duckDB.createTable("traces", gotTraces.columnarTraces);
          tableCreated = true;
        }
        if (!tableCreated) {
          await duckDB.insertIntoTable("traces", gotTraces.columnarTraces);
        }
      });
    }
    const dataUsage: DataUsage = gotTraces.dataUsage as DataUsage;

    context.commit(
      "setDataProcessed",
      context.getters["getDataProcessed"] + dataUsage.dataProcessed
    );
    context.commit(
      "setDataReturned",
      context.getters["getDataReturned"] + dataUsage.dataReturned
    );
    context.commit(
      "setTracesCount",
      context.getters["getTracesCount"] +
        getTracesCount(gotTraces.columnarTraces)
    );

    context.commit("incUpdateTraces");

    const hasNextPage = gotTraces.pageInfo.hasNextPage;
    const shouldConsumeNewFile = !hasNextPage;
    if (hasNextPage) {
      const updatedQueryParam = {
        ...input,
        pageCursor: gotTraces.pageInfo.endCursor,
      };
      QueryData.updateQueryData("traces", updatedQueryParam, false);
    } else {
      // Set the filename as consumed
      QueryData.updateQueryData("traces", input, true);
    }

    // Load new clean queryParam object
    const cleanQueryParam = loadingStrategy.getQueryInputs(
      "traces",
      1,
      shouldConsumeNewFile
    ) as GetFilteredTracesV2[];
    if (cleanQueryParam.length > 0)
      await executeTracesQuery(context, cleanQueryParam[0], thisQueryStartedAt);
  } catch (e: any) {
    const error = e.graphQLErrors ? e.graphQLErrors[0] : e;
    return {
      message: error.message,
    };
  }
}

/**
 * Auxiliary function used to stop the parallel queries from running when the environment conditions change.
 * @param context - Traces Vuex context from where the function is being called.
 * @param thisQueryStartedAt - DateTime at which time this query began executing.
 * @returns Indicates whether the current query should be stopped or not.
 */
function shouldStopQuery(
  context: ActionContext<TracesState, RootState>,
  thisQueryStartedAt: DateTime
): boolean {
  const storeLatestQueryStartedAt = computed<DateTime | undefined>(
    () => context.getters["getLatestQueryStartedAt"]
  );
  const storeShouldExecuteQuery = computed(
    () => context.getters["getShouldExecuteQuery"]
  );

  const newerQueryStarted =
    storeLatestQueryStartedAt.value !== undefined &&
    storeLatestQueryStartedAt.value > thisQueryStartedAt;
  const queryShouldCancel =
    storeShouldExecuteQuery.value !== undefined &&
    storeShouldExecuteQuery.value === false;

  return newerQueryStarted || queryShouldCancel;
}

/**
 * Auxiliary function that returns true when the current traces count exceeds the fetchLimit value
 * @param context - Traces Vuex context from where the function is being called.
 * @returns Indicates whether the current fetchLimit value as been reached/exceeded.
 */
function queryLimitReached(context: ActionContext<TracesState, RootState>) {
  return context.getters["getTracesCount"] >= context.getters["getFetchLimit"];
}

/**
 * Get the count of traces in the columnarTraces object.
 * @param columnarTraces - The ColumnarTraces object to count.
 * @returns The number of traces in the ColumnarTraces object.
 */
function getTracesCount(columnarTraces: ColumnarTraces): number {
  return columnarTraces["id"] !== undefined ? columnarTraces["id"].length : 0;
}
